import { querySelector, querySelectorAll } from "../../utility/utils";
import Swiper from 'swiper';
import { EmptyNodeList } from '../../utility/utils/index';

// A base class for our components, it will make it easier to do some effect for all components at once
class BaseObject extends Object {

    get className(): string {
        return typeof this;
    }
}

declare global {
    interface ExtendedSwiper extends Swiper {
        initialized: boolean;
    }
    interface HTMLElement { swiper: ExtendedSwiper; }

}



class BaseElements<Selectors extends BaseObject> {
    wrapper?: HTMLElement;

    constructor(readonly selectors: Selectors, immediate: boolean) {
        if (immediate) {
            this.initElements();
        }
    }

    initElements(): void {

    };


    /**
     * Instead of intializing each element manually, we can automatically initialize the elements by this way
     * 
     * @param wrapper 
     */
    initElementsByLoop(wrapper: HTMLElement = null): void {
        Object.keys(this.selectors).forEach(key => {
            const selector = this.selectors[key];
            if (this.typeOfNodeList(this[key])) {
                this.setToQuerySelectorAll(key, selector, wrapper);
            } else {
                this[key] = querySelector(selector, wrapper);
            }
        })
    }

    setToQuerySelectorAll(key: string, selector, wrapper) {
        try {
            if (this[key] instanceof EmptyNodeList) {
                this[key] = []
            }
            this[key] = querySelectorAll(selector, wrapper);
        } catch (e) {

        }
    }

    typeOfNodeList(obj): boolean {
        return obj instanceof EmptyNodeList || NodeList.prototype.isPrototypeOf(obj) || HTMLCollection.prototype.isPrototypeOf(obj)
    }

    initElementsFromWapperByLoop(): void {
        this.initElementsByLoop(this.wrapper);
    }

}

interface BaseService<Selectors extends BaseObject, Elements extends BaseElements<Selectors>, ClassList extends BaseObject, Texts extends BaseObject> {
    base: Base<Elements, ClassList, Texts>;

    get selectors(): Selectors;
    get classList(): ClassList;
    get elements(): Elements;
    get texts(): Texts;
}

class Base<Elements extends BaseElements<BaseObject>, ClassList extends BaseObject, Texts extends BaseObject>  {

    constructor(readonly elements: Elements, readonly classList: ClassList, readonly texts: Texts) { }

    convertToArray<T extends HTMLElement>(htmlCollection: NodeListOf<HTMLElement> | HTMLCollection): T[] {
        return Array.prototype.slice.call(htmlCollection);
    }

    stringToNode(string): Element {
        const tempParent = document.createElement('div');
        tempParent.innerHTML = string.trim();
        return tempParent.firstElementChild;
    }
}


function BaseInjector<Selectors extends BaseObject, Elements extends BaseElements<Selectors>, ClassList extends BaseObject, Texts extends BaseObject>
    (selectors: { new(): Selectors }, elements: { new(selectors: Selectors): Elements }, classList: { new(): ClassList }, texts: { new(): Texts }) {

    return function <T extends { new(...args: any[]): {} }>(constructor: T) {
        return class extends constructor {
            base = new Base(new elements(new selectors()), new classList(), new texts());
            get selectors(): Selectors {
                return this.base.elements.selectors;
            }
            get classList(): ClassList {
                return this.base.classList;
            }
            get elements(): Elements {
                return this.base.elements;
            }
            get texts(): Texts {
                return this.base.texts;
            }
        }
    }
}

export default Base;

export {
    BaseObject,
    BaseElements,
    BaseService,
    BaseInjector,
}